import React, { useCallback, useEffect, useState } from 'react'
import { useReactFlow, Node, Edge } from '@xyflow/react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Link } from 'lucide-react'

interface NodeConnectionMenuProps {
  nodeId: string
  onConnect: (sourceId: string, targetId: string) => void
  onDisconnect: (edgeId: string) => void
}

export const NodeConnectionMenu: React.FC<NodeConnectionMenuProps> = ({
  nodeId,
  onConnect,
  onDisconnect,
}) => {
  const { getNodes, getEdges } = useReactFlow()
  const [availableNodes, setAvailableNodes] = useState<Node[]>([])
  const [connectedNodes, setConnectedNodes] = useState<{
    source: Node[]
    target: Node[]
  }>({ source: [], target: [] })

  useEffect(() => {
    const nodes = getNodes()
    const edges = getEdges()
    
    // Filter out the current node and get available nodes
    const available = nodes.filter(node => node.id !== nodeId)
    setAvailableNodes(available)

    // Get connected nodes
    const sourceNodes: Node[] = []
    const targetNodes: Node[] = []

    edges.forEach(edge => {
      if (edge.source === nodeId) {
        const targetNode = nodes.find(n => n.id === edge.target)
        if (targetNode) sourceNodes.push(targetNode)
      }
      if (edge.target === nodeId) {
        const sourceNode = nodes.find(n => n.id === edge.source)
        if (sourceNode) targetNodes.push(sourceNode)
      }
    })

    setConnectedNodes({ source: sourceNodes, target: targetNodes })
  }, [nodeId, getNodes, getEdges])

  const handleConnect = useCallback((targetId: string) => {
    onConnect(nodeId, targetId)
  }, [nodeId, onConnect])

  const handleDisconnect = useCallback((edgeId: string) => {
    onDisconnect(edgeId)
  }, [onDisconnect])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-gray-100 rounded">
        <Link className="w-4 h-4" />
        Connections
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>Connect to Node</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Available Nodes */}
        {availableNodes.length > 0 ? (
          availableNodes.map(node => (
            <DropdownMenuItem
              key={node.id}
              onClick={() => handleConnect(node.id)}
              className="flex items-center justify-between"
            >
              <span>{node.data?.label || node.id}</span>
              <span className="text-xs text-gray-500">
                {node.type || 'Unknown'}
              </span>
            </DropdownMenuItem>
          ))
        ) : (
          <DropdownMenuItem disabled>No available nodes</DropdownMenuItem>
        )}

        {/* Current Connections */}
        {(connectedNodes.source.length > 0 || connectedNodes.target.length > 0) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Current Connections</DropdownMenuLabel>
            
            {/* Outgoing Connections */}
            {connectedNodes.source.length > 0 && (
              <>
                <DropdownMenuLabel className="text-xs text-gray-500">
                  Outgoing
                </DropdownMenuLabel>
                {connectedNodes.source.map(node => (
                  <DropdownMenuItem
                    key={node.id}
                    className="flex items-center justify-between"
                  >
                    <span>{node.data?.label || node.id}</span>
                    <span className="text-xs text-gray-500">
                      {node.type || 'Unknown'}
                    </span>
                  </DropdownMenuItem>
                ))}
              </>
            )}

            {/* Incoming Connections */}
            {connectedNodes.target.length > 0 && (
              <>
                <DropdownMenuLabel className="text-xs text-gray-500">
                  Incoming
                </DropdownMenuLabel>
                {connectedNodes.target.map(node => (
                  <DropdownMenuItem
                    key={node.id}
                    className="flex items-center justify-between"
                  >
                    <span>{node.data?.label || node.id}</span>
                    <span className="text-xs text-gray-500">
                      {node.type || 'Unknown'}
                    </span>
                  </DropdownMenuItem>
                ))}
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
} 